import { z } from 'zod';

import {
  academicStatusSchema,
  gradingScaleKindSchema,
  jornadaSchema,
  nationalScaleBandSchema,
  schoolCalendarSchema,
} from '../domain/types';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const name = z.string().trim().min(1).max(120);
const uuid = z.string().uuid();

/**
 * Pagination every list endpoint accepts.
 */
export const pageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/**
 * Pagination query values.
 */
export type PageQuery = z.output<typeof pageQuerySchema>;

/**
 * A sede (campus).
 */
export const createSedeInputSchema = z.object({
  name,
  address: z.string().trim().max(300).optional(),
  daneSedeCode: z.string().trim().max(32).optional(),
  isMain: z.boolean().default(false),
});
export const updateSedeInputSchema = createSedeInputSchema.partial();
/**
 * Inputs for sedes.
 */
export type CreateSedeInput = z.input<typeof createSedeInputSchema>;
/**
 * Editable sede fields.
 */
export type UpdateSedeInput = z.input<typeof updateSedeInputSchema>;

/**
 * A school year.
 */
export const createSchoolYearInputSchema = z
  .object({
    name,
    calendar: schoolCalendarSchema.default('A'),
    startsOn: isoDate,
    endsOn: isoDate,
  })
  .refine((value) => value.startsOn < value.endsOn, {
    message: 'endsOn must follow startsOn',
    path: ['endsOn'],
  });
export const updateSchoolYearInputSchema = z
  .object({
    name,
    calendar: schoolCalendarSchema,
    startsOn: isoDate,
    endsOn: isoDate,
    status: academicStatusSchema,
  })
  .partial();
/**
 * Inputs for school years.
 */
export type CreateSchoolYearInput = z.input<typeof createSchoolYearInputSchema>;
/**
 * Editable school year fields.
 */
export type UpdateSchoolYearInput = z.input<typeof updateSchoolYearInputSchema>;

/**
 * An academic period inside a school year.
 */
export const createPeriodInputSchema = z
  .object({
    schoolYearId: uuid,
    name,
    position: z.number().int().min(1).max(12),
    startsOn: isoDate,
    endsOn: isoDate,
  })
  .refine((value) => value.startsOn < value.endsOn, {
    message: 'endsOn must follow startsOn',
    path: ['endsOn'],
  });
export const updatePeriodInputSchema = z
  .object({ name, position: z.number().int().min(1).max(12), startsOn: isoDate, endsOn: isoDate })
  .partial();
/**
 * Inputs for periods.
 */
export type CreatePeriodInput = z.input<typeof createPeriodInputSchema>;
/**
 * Editable period fields.
 */
export type UpdatePeriodInput = z.input<typeof updatePeriodInputSchema>;

/**
 * A level (Preescolar, Primaria, …).
 */
export const createLevelInputSchema = z.object({ name, position: z.number().int().min(1).max(50) });
export const updateLevelInputSchema = createLevelInputSchema.partial();
/**
 * Inputs for levels.
 */
export type CreateLevelInput = z.input<typeof createLevelInputSchema>;
/**
 * Editable level fields.
 */
export type UpdateLevelInput = z.input<typeof updateLevelInputSchema>;

/**
 * A grade inside a level.
 */
export const createGradeInputSchema = z.object({
  levelId: uuid,
  name,
  position: z.number().int().min(1).max(50),
});
export const updateGradeInputSchema = z
  .object({ levelId: uuid, name, position: z.number().int().min(1).max(50) })
  .partial();
/**
 * Inputs for grades.
 */
export type CreateGradeInput = z.input<typeof createGradeInputSchema>;
/**
 * Editable grade fields.
 */
export type UpdateGradeInput = z.input<typeof updateGradeInputSchema>;

/**
 * A group of one grade in one sede and jornada during one school year.
 */
export const createGroupInputSchema = z.object({
  schoolYearId: uuid,
  sedeId: uuid,
  gradeId: uuid,
  name,
  jornada: jornadaSchema.default('unica'),
});
export const updateGroupInputSchema = z
  .object({ sedeId: uuid, gradeId: uuid, name, jornada: jornadaSchema })
  .partial();
/**
 * Inputs for groups.
 */
export type CreateGroupInput = z.input<typeof createGroupInputSchema>;
/**
 * Editable group fields.
 */
export type UpdateGroupInput = z.input<typeof updateGroupInputSchema>;

/**
 * A subject.
 */
export const createSubjectInputSchema = z.object({
  name,
  code: z.string().trim().max(32).optional(),
});
export const updateSubjectInputSchema = createSubjectInputSchema.partial();
/**
 * Inputs for subjects.
 */
export type CreateSubjectInput = z.input<typeof createSubjectInputSchema>;
/**
 * Editable subject fields.
 */
export type UpdateSubjectInput = z.input<typeof updateSubjectInputSchema>;

/**
 * A classroom in a sede.
 */
export const createClassroomInputSchema = z.object({
  sedeId: uuid,
  name,
  capacity: z.number().int().min(1).max(500).optional(),
});
export const updateClassroomInputSchema = createClassroomInputSchema.partial();
/**
 * Inputs for classrooms.
 */
export type CreateClassroomInput = z.input<typeof createClassroomInputSchema>;
/**
 * Editable classroom fields.
 */
export type UpdateClassroomInput = z.input<typeof updateClassroomInputSchema>;

/**
 * The grading scale of a school year, with its mapping to the national scale.
 */
export const gradingScaleInputSchema = z
  .object({
    kind: gradingScaleKindSchema.default('numeric'),
    minScore: z.number().min(0).max(1000).default(1),
    maxScore: z.number().min(0).max(1000).default(5),
    passMark: z.number().min(0).max(1000).default(3),
    /**
     * Decimals a score and a period grade carry on this scale (#106): 1 for 1.0–5.0, 0 for a
     * scale of whole numbers. Left out, the scale keeps what it had (1 for a new one).
     */
    decimals: z.number().int().min(0).max(2).optional(),
    nationalBands: z.array(nationalScaleBandSchema).min(1).optional(),
  })
  .refine((value) => value.minScore < value.maxScore, {
    message: 'maxScore must exceed minScore',
    path: ['maxScore'],
  })
  .refine((value) => value.passMark >= value.minScore && value.passMark <= value.maxScore, {
    message: 'passMark must be within the scale',
    path: ['passMark'],
  });
/**
 * Input of `PUT /school/years/{id}/grading-scale`.
 */
export type GradingScaleInput = z.input<typeof gradingScaleInputSchema>;
